import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { basketMutations, checkoutQueryKeys } from '@as-react/common-queries';
import { axiosClient } from '@as-react/common-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Divider, Link, Prose, Stack, TextAlpha } from '@yonderland/andes-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { useCheckoutConfig } from '../../../context/CheckoutContext';
import { useCheckoutStepState, CheckoutStep } from '../../../context/CheckoutStepContext';
import { useOnVoucherChanges } from '../../../hooks/useOnVoucherChanges';
import { basketActions } from '../../../redux/basket/actions/BasketActions.creators';
import { makeGetBasketLockState, makeGetBasketMappedProductsState, makeGetBasketPricingState, makeGetBasketTotalQuantityState, makeGetBasketVouchersState, } from '../../../redux/basket/selectors/BasketSelector';
import { CartTotal } from '../../cart-total/CartTotal';
import { ProductTileOrderOverview } from '../../product-tile/order-overview/ProductTileOrderOverview';
export const ProductsSummary = () => {
    const { t } = useTranslation([I18N_NAMESPACES.checkout]);
    const { defaultRequestParams, servicesEndpoint, root } = useAemConfig();
    const { step } = useCheckoutStepState();
    const { termsUrl } = useCheckoutConfig();
    const products = useSelector(makeGetBasketMappedProductsState());
    const vouchers = useSelector(makeGetBasketVouchersState());
    const pricing = useSelector(makeGetBasketPricingState());
    const isBasketLocked = useSelector(makeGetBasketLockState());
    const basketTotalQuantity = useSelector(makeGetBasketTotalQuantityState());
    const reduxDispatch = useDispatch();
    const getProducts = () => reduxDispatch(basketActions.createGetAction());
    const { onVoucherChanges } = useOnVoucherChanges();
    const queryClient = useQueryClient();
    const { deleteVoucher } = basketMutations({ defaultRequestParams, servicesEndpoint });
    const { mutate: deleteVoucherMutation } = useMutation(deleteVoucher);
    const handleDeleteVoucher = (barcode) => {
        deleteVoucherMutation(barcode, {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                getProducts();
                if (step === CheckoutStep.PAYMENT) {
                    yield queryClient.refetchQueries({ queryKey: checkoutQueryKeys.getPaymentInfo() }); // this will trigger paymentInfo update in CheckoutPaymentContainer
                    yield onVoucherChanges();
                }
                // if (step === CheckoutStep.FAST_CHECKOUT) {
                //   getFastOverviewInfo();
                //   onVoucherChanges();
                // }
            }),
        });
    };
    const { data: terms } = useQuery({
        queryKey: ['terms', termsUrl],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const axiosConfig = {
                method: 'GET',
                url: termsUrl,
                withCredentials: true,
            };
            const result = yield axiosClient.request(axiosConfig);
            return result.data;
        }),
        enabled: !!termsUrl,
    });
    if (!products) {
        return null;
    }
    return (_jsxs(Stack, { direction: "col", gap: "4", children: [_jsxs(Stack, { wrap: "wrap", gap: "2", align: "center", children: [_jsx(TextAlpha, { variant: "subheadingS", children: t(`${I18N_NAMESPACES.checkout}:checkout.order.title.page`) }), !isBasketLocked && (_jsx(Link, { theme: "neutral", variant: "secondary", href: `${root}checkout/basket-overview.html`, testId: "back_to_basket_overview", children: t(`${I18N_NAMESPACES.buy}:buy.edit`) }))] }), _jsx(Stack, { direction: "col", gap: "3", children: products.map(product => (_jsxs(Fragment, { children: [_jsx(ProductTileOrderOverview, { product: product }), _jsx(Divider, {})] }, `${product.productCode}-${product.sku}`))) }), _jsx(CartTotal, Object.assign({ vouchers: vouchers }, pricing, { totalQuantity: basketTotalQuantity, onDeleteVoucher: handleDeleteVoucher })), terms && (_jsx(TextAlpha, { variant: "bodyS", children: _jsx(Prose, { html: terms }) }))] }));
};
